import { Inject, Injectable, NgZone, OnDestroy } from "@angular/core";
import { BehaviorSubject, Subject, filter, takeUntil } from "rxjs";
import { ExtractionService } from "./extraction.service";
import { AppConfigService } from "src/app/services/app-config.service";
import { MESSAGES } from "src/app/shared/constants/messages.constant";
import { RetryService } from "./retry.service";
import { ComparisonNormalizer } from "src/app/shared/utils/comparison-normalizer.util";
import { OKTA_AUTH } from "@okta/okta-angular";
import OktaAuth from "@okta/okta-auth-js";

@Injectable({
  providedIn: "root",
})
export class EvaluationService implements OnDestroy {
  private readonly evaluationWebsocketUrl: string = "";
  private readonly showConsoleLogs: boolean = false;
  //evaluation data
  private readonly evaluationDataSubject = new BehaviorSubject<any>(null);
  evaluationData$ = this.evaluationDataSubject.asObservable();

  private readonly evaluationMap = new Map<string, any>();
  private readonly evaluationMapSubject = new BehaviorSubject<any[]>([]);
  evaluationResult$ = this.evaluationMapSubject.asObservable();

  private readonly evaluationSummarySubject = new BehaviorSubject<any>(null);
  evaluationSummary$ = this.evaluationSummarySubject.asObservable();

  private readonly hiddenIndexesEquivalencySubject = new BehaviorSubject<
    number[]
  >([]);
  hiddenIndexesEquivalency$ =
    this.hiddenIndexesEquivalencySubject.asObservable();

  private socket?: WebSocket;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly extractionService: ExtractionService,
    private readonly envService: AppConfigService,
    private readonly zone: NgZone,
    private readonly retryService: RetryService,
    @Inject(OKTA_AUTH) private readonly oktaAuth: OktaAuth,
  ) {
    this.evaluationWebsocketUrl = this.envService.mmpEvaluationWebsocketUrl;
    this.showConsoleLogs = this.envService.showConsoleLogs;
    this.getRetryResults();
  }

  startWebSocket() {
    const transcripts = this.extractionService.getTranscriptMapValues();
    if (this.showConsoleLogs) {
      console.log("Transcripts from ExtractionService:", transcripts);
    }

    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.close();
    }

    this.socket = new WebSocket(this.evaluationWebsocketUrl);

    this.socket.onopen = async () => {
      const accessToken = this.oktaAuth.getAccessToken();

      this.socket.send(
        JSON.stringify({
          type: "auth",
          token: accessToken,
        }),
      );

      const payload = [];

      for (const transcript of transcripts) {
        const transcriptEach = {
          session_key: transcript.sessionKey,
          extraction_checkpoint_id: transcript.checkpoint,
          extracted_changed_data: transcript.output,
          proceed_to_next_stage: transcript.eligibleForProceeding,
        };
        payload.push(transcriptEach);
      }

      const transcriptData = JSON.stringify(payload);
      this.socket.send(transcriptData);

      const transcriptVersionMap =
        this.extractionService.getTranscriptVersion();

      if (transcriptVersionMap.size > 0) {
        const versionPayload = {
          type: "transcript_version",
          state: "extraction",
          data: Array.from(transcriptVersionMap.entries()).map(
            ([key, value]) => value,
          ),
        };
        this.socket.send(JSON.stringify(versionPayload));
      }

      const intervalId = setInterval(() => {
        if (this.socket.readyState === WebSocket.OPEN) {
          this.socket.send(
            JSON.stringify({
              type: MESSAGES.WEBSOCKET.HANDSHAKE_TYPE,
              text: MESSAGES.WEBSOCKET.UPLOAD_HANDSHAKE_MESSAGE,
            }),
          ); //Handshake senting to backend(server)
          if (this.showConsoleLogs) {
            console.log(MESSAGES.WEBSOCKET.FIXED_MESSAGE_SENT);
          }
        } else {
          if (this.showConsoleLogs) {
            console.log(MESSAGES.WEBSOCKET.HANDSHAKE_STOPED);
          }

          clearInterval(intervalId);
        }
      }, 10000);
    };

    this.socket.onmessage = (event) => {
      // Ensure Angular change detection runs
      this.zone.run(() => this.handleMessage(event));
    };

    this.socket.onerror = (error) => {
      if (this.showConsoleLogs) {
        console.error("WebSocket error:", error);
      }
    };

    this.socket.onclose = (event) => {
      const currentValue = this.getEvaluationMapValues();
      if (event.wasClean) {
        this.handleCleanClose(event, currentValue);
      } else {
        this.handleUnexpectedClose(event, currentValue);
      }
    };
  }

  private handleCleanClose(event: CloseEvent, currentValue: any[]): void {
    if (this.showConsoleLogs) {
      console.log(MESSAGES.WEBSOCKET.CLOSED_CLEAN, event.code, event.reason);
    }
    if (
      ComparisonNormalizer.string(event.reason) === "keepalive ping timeout"
    ) {
      this.retryService.checkRetryRequired(currentValue, "pre_evaluation");
    }
  }

  private handleUnexpectedClose(event: CloseEvent, currentValue: any[]): void {
    if (this.showConsoleLogs) {
      console.warn(
        MESSAGES.WEBSOCKET.CLOSED_UNEXPECTED,
        event.code,
        event.reason,
      );
    }
    if (currentValue.length === 0) {
      if (this.showConsoleLogs) {
        console.log("Websocket closed unexpectedly, retry evaluation ws");
      }
      this.evaluationRetry();
    } else {
      this.retryService.checkRetryRequired(currentValue, "pre_evaluation");
    }
  }

  private handleMessage(event: MessageEvent) {
    const data = JSON.parse(event.data);
    const sessionKey = data.session_key;

    this.processResumeStarted(data.status, sessionKey);
    this.confirmEvaluationMapEntry(sessionKey);
    this.processPreEvaluationOutput(data, sessionKey);
    this.processPreEvaluationCheckpoint(data, sessionKey);
    this.processFinalSummary(data);
    this.handleCompletionStatus(data.status);
  }

  public initializeEvaluationMapPending(sessionKey: string): void {
    // Create map entry at "started" time to preserve insertion order
    // Mark as pending until pre_evaluation_outputs confirms it
    if (!this.evaluationMap.has(sessionKey)) {
      this.evaluationMap.set(sessionKey, {
        sessionKey,
        status: "pending", // Mark as pending, not "started"
        output: "",
        checkpoint: "",
        eligibleForProceeding: true,
        isPending: true, // Flag to indicate this is not yet confirmed
      });
      // Do NOT emit yet - only emit after pre_evaluation_outputs confirms
    }
  }

  public confirmEvaluationMapEntry(sessionKey: string): void {
    // Confirm the pending entry when pre_evaluation_outputs arrives
    // Update status from "pending" to "started" and emit
    const transcript = this.evaluationMap.get(sessionKey);
    if (transcript && (transcript as any).isPending) {
      transcript.status = "started";
      delete (transcript as any).isPending;
      this.emit(); // Emit only confirmed entries
    }
  }

  public resolvePendingEvaluationMapEntry(sessionKey: string): void {
    const transcript = this.evaluationMap.get(sessionKey);
    if (!transcript || !(transcript as any).isPending) {
      return;
    }

    this.evaluationMap.delete(sessionKey);
    this.emit();
  }

  public processResumeStarted(status: string, sessionKey: string): void {
    if (ComparisonNormalizer.string(status) === "resume_started") {
      // Map is now initialized in data.service when "started" arrives
      // This method kept for backward compatibility
      const transcript = this.evaluationMap.get(sessionKey);
      if (!transcript) {
        this.evaluationMap.set(sessionKey, {
          sessionKey,
          status: "started",
          output: "",
          checkpoint: "",
          eligibleForProceeding: true,
        });
      }
    }
  }

  public processPreEvaluationOutput(data: any, sessionKey: string): void {
    if (ComparisonNormalizer.string(data.node) !== "pre_evaluation_outputs") {
      return;
    }

    const transcript = this.evaluationMap.get(sessionKey);
    if (!transcript) {
      return;
    }

    transcript.status = "completed";
    transcript.output = data.output;

    if (
      !transcript.output.result.pre_evaluation_passed &&
      transcript.output.result.pre_evaluation_passed !== "false"
    ) {
      this.emit();
    }
  }

  public processPreEvaluationCheckpoint(data: any, sessionKey: string): void {
    if (
      ComparisonNormalizer.string(data.checkpoint_type) !==
      "pre_evaluation_checkpoint"
    ) {
      return;
    }

    const transcript = this.evaluationMap.get(sessionKey);
    if (transcript) {
      transcript.checkpoint = data.pre_evaluation_checkpoint_id;
    }
  }

  public processFinalSummary(data: any): void {
    if (ComparisonNormalizer.string(data.type) === "final_summary") {
      this.evaluationSummarySubject.next(data.data);
    }
  }

  private handleCompletionStatus(status: string): void {
    if (ComparisonNormalizer.string(status) !== "completed") {
      return;
    }

    const allCompleted = Array.from(this.evaluationMap.values()).every(
      (transcript) =>
        ComparisonNormalizer.string(transcript.status) === "completed",
    );

    if (allCompleted) {
      if (this.showConsoleLogs) {
        console.log("All transcripts are completed. Closing WebSocket...");
      }
      this.disconnect();
      return;
    }

    if (this.showConsoleLogs) {
      console.log(MESSAGES.RETRY.RETRY_REQUIRED);
    }
    this.disconnect();
    const currentValue = this.getEvaluationMapValues();
    this.retryService.checkRetryRequired(currentValue, "pre_evaluation");
  }

  private emit() {
    // Emit only confirmed entries, filter out pending ones
    const confirmedEntries = Array.from(this.evaluationMap.values()).filter(
      (entry: any) => !entry.isPending,
    );
    this.evaluationMapSubject.next(confirmedEntries);
  }

  disconnect() {
    this.socket?.close();
  }

  setEvaluationData(data: any) {
    this.evaluationDataSubject.next(data);
  }

  getEvaluationData() {
    return this.evaluationDataSubject.value;
  }

  updateEvaluationOutput(
    sessionKey: string,
    newOutput: any,
    eligibleForProceeding?: boolean,
  ): void {
    // Check if the sessionKey exists in the evaluationMap
    const transcript = this.evaluationMap.get(sessionKey);
    if (!transcript) {
      if (this.showConsoleLogs) {
        console.error(`Transcript with sessionKey ${sessionKey} not found.`);
      }
      return;
    }
    // Update the output of the transcript
    transcript.output.result.transcript_rule_evaluation = newOutput;
    if (eligibleForProceeding !== undefined) {
      transcript.eligibleForProceeding = eligibleForProceeding;
      transcript.output.result["eligibleForProceeding"] = eligibleForProceeding;
    }
  }

  clearEvaluationResult() {
    this.evaluationMap.clear();
    this.evaluationMapSubject.next([]);
    this.evaluationDataSubject.next(null);
    this.evaluationSummarySubject.next(null);
  }

  getEvaluationMapValues(): any {
    // Return only confirmed entries, filter out pending ones
    return Array.from(this.evaluationMap.values()).filter(
      (entry: any) => !entry.isPending,
    );
  }

  getRetryResults() {
    this.retryService.retryResult$
      .pipe(
        takeUntil(this.destroy$),
        filter(
          (result) =>
            ComparisonNormalizer.string(result.type) === "pre_evaluation",
        ),
      )
      .subscribe((result) => {
        const data = result.payload;
        const sessionKey = data.session_key;

        if (
          ComparisonNormalizer.string(data.node) === "pre_evaluation_outputs"
        ) {
          const transcript = this.evaluationMap.get(sessionKey);
          if (transcript) {
            transcript.status = "completed";
            transcript.output = data.output;
          }
          this.emit();
        }

        if (
          ComparisonNormalizer.string(data.checkpoint_type) ===
          "pre_evaluation_checkpoint"
        ) {
          const transcript = this.evaluationMap.get(sessionKey);
          if (transcript) {
            transcript.checkpoint = data.pre_evaluation_checkpoint_id;
          }
        }

        if (ComparisonNormalizer.string(data.type) === "final_summary") {
          this.evaluationSummarySubject.next(data.data);
        }
      });
  }

  evaluationRetry() {
    this.retryService.showAgentRetryConfirmationDialog().then((response) => {
      if (ComparisonNormalizer.boolean(response) === true) {
        this.startWebSocket();
      }
    });
  }

  disconnectRetry(): void {
    this.retryService.disconnect();
  }

  setSavedHiddenIndexesEquivalency(): void {
    const hiddenIndexes = Array.from(this.evaluationMap.values())
      .map((transcript: any, index: number) => {
        return transcript?.output?.result?.eligibleForProceeding === false
          ? index
          : null;
      })
      .filter((index) => index !== null) as number[];

    this.hiddenIndexesEquivalencySubject.next(hiddenIndexes);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
